import { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { useWorkspaceAddons } from "@/hooks/useWorkspaceAddons";
import { useWorkspace } from "@/hooks/useWorkspace";
import { WORKSPACE_ADDON_PRICE } from "@/lib/tier";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Standalone embedded-checkout dialog for purchasing one extra workspace
 * add-on seat. Polls workspace state every 3s while open so the
 * "Manage Add-Ons" card and the workspace switcher refresh as soon as
 * the Stripe webhook lands.
 */
export function AddWorkspaceSeatDialog({ open, onOpenChange }: Props) {
  const { refresh } = useWorkspace();
  const { count: addonCount, refresh: refreshAddons } = useWorkspaceAddons();
  const baseline = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      baseline.current = null;
      return;
    }
    baseline.current = addonCount;
    void refresh();
    void refreshAddons();

    const tick = async () => {
      await Promise.all([refresh(), refreshAddons()]);
    };
    const interval = window.setInterval(tick, 3000);
    const onFocus = () => { void tick(); };
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
    // Baseline must capture the count at the moment the dialog opens —
    // intentionally only re-run on open transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const start = baseline.current;
    if (start !== null && addonCount > start) {
      toast.success("Workspace seat added");
      onOpenChange(false);
    }
  }, [open, addonCount, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a workspace seat</DialogTitle>
          <DialogDescription>
            ${WORKSPACE_ADDON_PRICE.monthly}/month per extra workspace, billed
            monthly. You'll be able to use the new seat as soon as payment
            confirms.
          </DialogDescription>
        </DialogHeader>
        <StripeEmbeddedCheckout priceId="workspace_addon_monthly" />
      </DialogContent>
    </Dialog>
  );
}
